// PATCH /api/finance/processors/{id}/settlement-aging-threshold and
// GET /api/finance/processors/{id}/threshold-impact. The settings page renders the affected-row
// count next to each threshold; the edit modal commits and emits the
// PaymentProcessorThresholdUpdated audit.
import type {
  ProcessorThresholdImpactDto,
  UpdateProcessorThresholdRequest,
  UpdateProcessorThresholdResult,
} from '@signalvote/shared';
import { skip } from '@signalvote/data';
import { apiJson } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { refusal } from './useFinanceContributions';
import { useRefreshFinanceAfter } from './useFinanceSettings';


/**
 * Count of in-flight (Reported + null-payout) contributions per processor. Powers the "This
 * affects {N} contributions currently waiting for settlement" subtitle on each settings row.
 */
const processorThresholdImpactRead = defineQuery({
  key: (processorId: string) => ['finance', 'processors', processorId, 'threshold-impact'],
  reads: 'GET /api/finance/processors/{id}/threshold-impact',
  fetch: (processorId, { signal, campaignId }) =>
    apiJson<ProcessorThresholdImpactDto>(`/api/finance/processors/${processorId}/threshold-impact`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch threshold impact',
    }),
});

export function useProcessorThresholdImpact(processorId: string | null | undefined) {
  return processorThresholdImpactRead(processorId || skip);
}

export interface UpdateProcessorThresholdVariables {
  processorId: string;
  body: UpdateProcessorThresholdRequest;
}

/**
 * Its write set refreshes the processor list, the impact count and the preflight. The processor
 * fee donations and the manual P2P deposits read the processor too and are not defined reads yet,
 * so it also refreshes the undeclared finance reads. A refusal carries the server's code and sentence.
 */
const updateProcessorThreshold = defineWrite({
  send: ({ processorId, body }: UpdateProcessorThresholdVariables) =>
    apiJson<UpdateProcessorThresholdResult>(`/api/finance/processors/${processorId}/settlement-aging-threshold`, {
      method: 'PATCH',
      json: body,
      error: refusal('Failed to update processor threshold'),
    }),
});

export function useUpdateProcessorThreshold() {
  return useRefreshFinanceAfter(updateProcessorThreshold());
}
